import React, { useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightToBracket, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ListColumnType, ListFilterType, ListRowType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import ListContext from 'components/lists/ListContext';
import { renderDateTime } from 'components/lists/functions/renderListColumn';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { hasAnyAllowedRight } from 'services/permissions/permissionChecks';
import { useSettings } from 'services/settings/SettingsContext';
import DemoEnvironmentForm from './DemoEnvironmentForm';
import DemoEnvironmentProgressModal from './DemoEnvironmentProgressModal';
import EnvironmentDeleteModal from './EnvironmentDeleteModal';
import '../../style/scss/demoEnvironment.scss';

/*
 * DemoEnvironmentList.tsx
 * Superuser and staff list of the sales demo environments. A demo
 * environment is created with a name, sector and package, the demo
 * data is generated in the background and the environment can be
 * opened or deleted again from this list.
 */

// Icon which opens the demo environment as the current superuser or staff user
const DemoEnvironmentOpenIcon: React.FC<{ row: ListRowType }> = ({ row }) => {
    const { t } = useTranslation();
    const { handleSwitchEnvironment } = useAuthContext();

    // Only created demo environments with an environment can be opened
    if (row.status !== 'created' || !row.environment_hash) return null;

    // Switch to the demo environment without triggering the row click
    const openEnvironment = (event: React.MouseEvent) => {
        event.stopPropagation();
        handleSwitchEnvironment(row.environment_hash as string);
    };

    return (
        <div className='demo-environment-open-icon tooltip-icon' onClick={openEnvironment}>
            <FontAwesomeIcon icon={faArrowRightToBracket} />
            <span className='tooltip'>{t('demo_environment.general.open_environment_tooltip')}</span>
        </div>
    );
};

// Icon which opens the deletion modal of the demo environment
const DemoEnvironmentDeleteIcon: React.FC<{ row: ListRowType }> = ({ row }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { refetchList } = useContext(ListContext);

    // Only demo environments the backend marks as deletable get the delete icon
    if (!row.is_deletable) return null;

    // Open the deletion modal with the demo endpoints, without triggering the row click
    const openDeleteModal = (event: React.MouseEvent) => {
        event.stopPropagation();
        initializeModal(
            <EnvironmentDeleteModal
                environmentId={row.id}
                environmentName={row.name}
                onFinished={refetchList}
                deleteEndpoint='delete_demo_environment_as_superuser'
                statusEndpoint='get_demo_environment_deletion_status_as_superuser'
            />,
            { modalSize: 'extra-small' }
        );
    };

    return (
        <div className='list-delete-icon tooltip-icon environment-delete-icon'>
            <FontAwesomeIcon
                icon={faTrash}
                className='fas fa-trash'
                style={{ cursor: 'pointer' }}
                onClick={openDeleteModal} />
            <span className='tooltip'>{t('demo_environment.general.delete_tooltip')}</span>
        </div>
    );
};

// Rename the name of the function:
const DemoEnvironmentList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { allowedRights } = useAuthContext();
    const { initializeModal } = useModal();

    // Keep the refetch function of the list, the modals open outside the list context
    const refetchListRef = useRef<() => void>(() => {});
    const refetchList = () => refetchListRef.current();

    // Superusers and staff users may create demo environments
    const canCreate = hasAnyAllowedRight(allowedRights, ['no_user', 'staff_user']);

    // Specify the options of this list:
    const query = {
        endpoint: 'get_demo_environment_as_superuser_list',
        object: 'demo_environment_as_superuser'
    }
    const objectName = 'demo_environment.general.object_name'
    const showSearch = true

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Render the status of the demo environment with the progress while creating
    const renderStatus = (row: ListRowType) => {
        const deletionStatus = row.environment_deletion_status as string | undefined;
        if (deletionStatus === 'deleted' || deletionStatus === 'deleting') {
            return <span className={`demo-environment-status ${deletionStatus}`}>{t(`demo_environment.status.${deletionStatus}`)}</span>;
        }
        if (row.status === 'creating') {
            return <span className='demo-environment-status creating'>{t('demo_environment.status.creating')} ({row.progress ?? 0}%)</span>;
        }
        return <span className={`demo-environment-status ${row.status}`}>{t(`demo_environment.status.${row.status}`)}</span>;
    };

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { width: 'minmax(0, 2fr)', field: 'created', label: 'demo_environment.general.created_label', render: (_, row) => renderDateTime(row.created as string, userLocale) },
        { width: 'minmax(0, 3fr)', field: 'name', label: 'demo_environment.general.name_label', customClass: 'truncate-cell' },
        { width: 'minmax(0, 2fr)', field: 'industry', label: 'demo_environment.general.industry_label', customClass: 'truncate-cell', render: (value) => value ? t(`trial.industry.${value}`) : '' },
        { width: 'minmax(0, 3fr)', field: 'login_email', label: 'demo_environment.general.login_email_label', customClass: 'truncate-cell' },
        { width: 'minmax(0, 2fr)', field: 'created_by_name', label: 'demo_environment.general.created_by_label', customClass: 'truncate-cell', disableOrdering: true },
        { width: 'minmax(0, 2fr)', field: 'status', label: 'demo_environment.general.status_label', render: (_, row) => renderStatus(row) },
        { width: '2.5rem', field: 'environment_hash', label: '', customClass: 'icon-action-column', disableOrdering: true, render: (_, row) => <DemoEnvironmentOpenIcon row={row} /> },
        { width: '2rem', field: 'is_deletable', label: '', disableOrdering: true, render: (_, row) => <DemoEnvironmentDeleteIcon row={row} /> },
    ];

    // Open the progress modal when a creating or failed demo environment is clicked
    const onRowClick = (row: ListRowType) => {
        if (row.status === 'creating' || row.status === 'failed' || row.status === 'created') {
            initializeModal(
                <DemoEnvironmentProgressModal
                    demoEnvironmentId={row.id}
                    name={row.name}
                    loginEmail={row.login_email as string | null}
                    onFinished={refetchList}
                />,
                { modalSize: 'small' }
            );
        }
    };

    // Specify the filters of this list:
    const filters: ListFilterType[] = [
        {
            id: 'all',
            label: 'demo_environment.general.all_filter_label',
            params: {},
            default: true,
        },
        {
            group: 'general.archive',
            filters: [
                {
                    id: 'deleted',
                    label: 'demo_environment.general.deleted_filter_label',
                    shortName: 'general.deleted',
                    params: { 'deleted': true },
                }
            ]
        }
    ]

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            filters={filters}
            columns={columns}
            showSearch={showSearch}
            showAddButton={canCreate}
            formModal={<DemoEnvironmentForm onFinished={refetchList} />}
            modalSize='small'
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            setRefetchList={
                useCallback((fn: () => void) => {
                    refetchListRef.current = fn;
                }, [])
            }
        />
    );
};

export default DemoEnvironmentList;
